/**
 * UsersPagination — Industry-standard pagination control for the
 * Admin Users Management table (Feb 28, 2026).
 *
 * Layout:
 *   [ Showing 1–25 of 126 users ]   [« ‹  1 2 … 5  › »]
 *
 * Always renders when total_items > 0 (even for a single page) so
 * Admin sees the total count up-front — a recurring ask from Ops.
 *
 * Page-number rendering uses a windowed strategy: always show the
 * first, last, current, and ±1 neighbors, with ellipses for gaps.
 * This keeps the control compact even at 100+ pages.
 */

import React from 'react';
import { ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight } from 'lucide-react';
import { Button } from './ui/button';

// Build a windowed list of page numbers + ellipses. Always include
// first, last, current, and a small neighborhood — fills the middle
// with the literal string '…' for gaps.
function buildPageWindow(currentPage, totalPages, windowSize = 1) {
  if (totalPages <= 1) return [1];
  const pages = new Set([1, totalPages, currentPage]);
  for (let i = currentPage - windowSize; i <= currentPage + windowSize; i++) {
    if (i >= 1 && i <= totalPages) pages.add(i);
  }
  const sorted = Array.from(pages).sort((a, b) => a - b);
  const withGaps = [];
  let last = 0;
  for (const p of sorted) {
    if (last && p - last > 1) withGaps.push('…');
    withGaps.push(p);
    last = p;
  }
  return withGaps;
}

export const UsersPagination = ({
  page,
  pageSize,
  totalPages,
  totalItems,
  onPageChange,
  loading = false,
  itemLabel = 'users',
  pageSizeOptions = null,
  onPageSizeChange = null,
  testIdPrefix = 'users-pagination',
}) => {
  if (!totalItems) return null;

  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalItems);
  const isFirst = page <= 1;
  const isLast = page >= totalPages;
  const window = buildPageWindow(page, totalPages, 1);

  return (
    <div
      className="flex flex-wrap items-center justify-between gap-3 mt-4 pt-4 border-t border-slate-200"
      data-testid={testIdPrefix}
    >
      <div className="flex flex-wrap items-center gap-4">
        <div
          className="text-sm text-slate-600"
          data-testid={`${testIdPrefix}-summary`}
        >
          Showing <span className="font-medium text-slate-900">{start}</span>
          –<span className="font-medium text-slate-900">{end}</span> of{' '}
          <span className="font-medium text-slate-900">{totalItems}</span> {itemLabel}
        </div>
        {Array.isArray(pageSizeOptions) && onPageSizeChange && (
          <label className="flex items-center gap-2 text-sm text-slate-600">
            Rows per page:
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              disabled={loading}
              className="h-8 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid={`${testIdPrefix}-page-size`}
            >
              {pageSizeOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      <div className="flex items-center gap-1.5">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(1)}
          disabled={isFirst || loading}
          title="First page"
          data-testid={`${testIdPrefix}-first`}
        >
          <ChevronsLeft className="w-4 h-4" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page - 1)}
          disabled={isFirst || loading}
          data-testid={`${testIdPrefix}-prev`}
        >
          <ChevronLeft className="w-4 h-4" />
          <span className="hidden sm:inline ml-1">Previous</span>
        </Button>
        <div className="flex items-center gap-1" data-testid={`${testIdPrefix}-pages`}>
          {window.map((p, idx) =>
            p === '…' ? (
              <span
                key={`gap-${idx}`}
                className="px-2 text-slate-400 select-none"
                aria-hidden="true"
              >
                …
              </span>
            ) : (
              <Button
                key={p}
                variant={p === page ? 'default' : 'outline'}
                size="sm"
                onClick={() => onPageChange(p)}
                disabled={loading}
                aria-current={p === page ? 'page' : undefined}
                data-testid={`${testIdPrefix}-page-${p}`}
                className="min-w-[36px]"
              >
                {p}
              </Button>
            )
          )}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page + 1)}
          disabled={isLast || loading}
          data-testid={`${testIdPrefix}-next`}
        >
          <span className="hidden sm:inline mr-1">Next</span>
          <ChevronRight className="w-4 h-4" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(totalPages)}
          disabled={isLast || loading}
          title="Last page"
          data-testid={`${testIdPrefix}-last`}
        >
          <ChevronsRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

export default UsersPagination;
